import React, { useState, useEffect } from 'react';
import { warehousesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  Warehouse,
  Edit2,
  Trash2,
  Package,
  Loader2,
  MapPin
} from 'lucide-react';

const WarehousesPage = () => {
  const { isAdmin } = useAuth();
  const [warehouses, setWarehouses] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address: ''
  });
  const [saving, setSaving] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [warehouseStocks, setWarehouseStocks] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(false);

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const response = await warehousesApi.list();
      setWarehouses(response.data.data);
    } catch (error) {
      toast.error('Error al cargar almacenes');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStocks = async () => {
    try {
      const response = await warehousesApi.getAllStocks();
      setStocks(response.data.data);
    } catch (error) {
      console.error('Error fetching stocks:', error);
    }
  };

  const fetchWarehouseStocks = async (warehouseId) => {
    try {
      setStocksLoading(true);
      const response = await warehousesApi.getStocks(warehouseId);
      setWarehouseStocks(response.data);
    } catch (error) {
      toast.error('Error al cargar stock');
    } finally {
      setStocksLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
    fetchAllStocks();
  }, []);

  useEffect(() => {
    if (selectedWarehouse) {
      fetchWarehouseStocks(selectedWarehouse.id);
    }
  }, [selectedWarehouse]);

  const openCreateDialog = () => {
    setEditingWarehouse(null);
    setFormData({ code: '', name: '', address: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      code: warehouse.code,
      name: warehouse.name,
      address: warehouse.address || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingWarehouse) {
        await warehousesApi.update(editingWarehouse.id, {
          name: formData.name,
          address: formData.address
        });
        toast.success('Almacén actualizado');
      } else {
        await warehousesApi.create(formData);
        toast.success('Almacén creado');
      }
      setDialogOpen(false);
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (warehouse) => {
    if (!window.confirm(`¿Eliminar el almacén "${warehouse.name}"?`)) return;

    try {
      await warehousesApi.delete(warehouse.id);
      toast.success('Almacén eliminado');
      fetchWarehouses();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="warehouses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Almacenes</h1>
          <p className="text-slate-500 mt-1">Gestiona los almacenes y su inventario</p>
        </div>
        {isAdmin() && (
          <Button 
            onClick={openCreateDialog}
            className="bg-blue-900 hover:bg-blue-800"
            data-testid="create-warehouse-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Almacén
          </Button>
        )}
      </div>

      <Tabs defaultValue="warehouses" className="space-y-6">
        <TabsList>
          <TabsTrigger value="warehouses" className="flex items-center gap-2">
            <Warehouse className="w-4 h-4" />
            Almacenes
          </TabsTrigger>
          <TabsTrigger value="stock" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Inventario Global
          </TabsTrigger>
        </TabsList>

        {/* Warehouses List */}
        <TabsContent value="warehouses">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {warehouses.map((warehouse) => (
                <Card key={warehouse.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-sm bg-blue-900">
                          <Warehouse className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{warehouse.name}</CardTitle>
                          <p className="text-sm text-slate-500 font-mono">{warehouse.code}</p>
                        </div>
                      </div>
                      <Badge className={warehouse.is_active 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                        : 'bg-slate-100 text-slate-600'
                      }>
                        {warehouse.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {warehouse.address && (
                      <p className="text-sm text-slate-600 flex items-start gap-2 mb-4">
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {warehouse.address}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedWarehouse(warehouse)}
                        className="text-blue-900"
                      >
                        Ver Stock
                      </Button>
                      {isAdmin() && (
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(warehouse)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(warehouse)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Global Stock */}
        <TabsContent value="stock">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Inventario Global</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Producto</th>
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Almacén</th>
                      <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Cantidad</th>
                      <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Stock Mín.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stocks.map((stock, idx) => (
                      <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <p className="text-sm font-medium text-slate-900">{stock.product_name}</p>
                          <p className="text-xs text-slate-500 font-mono">{stock.product_sku}</p>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{stock.warehouse_name}</td>
                        <td className={`py-3 px-4 text-sm text-right tabular-nums font-medium ${
                          stock.quantity < stock.stock_min ? 'text-red-600' : 'text-slate-900'
                        }`}>
                          {stock.quantity}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500 text-right tabular-nums">
                          {stock.stock_min}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Warehouse Stock Dialog */}
      <Dialog open={!!selectedWarehouse} onOpenChange={() => setSelectedWarehouse(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Stock en {selectedWarehouse?.name}</DialogTitle>
          </DialogHeader>
          {stocksLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
            </div>
          ) : warehouseStocks.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay productos en este almacén</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Producto</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Cantidad</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Stock Mín.</th>
                  </tr>
                </thead>
                <tbody>
                  {warehouseStocks.map((stock, idx) => (
                    <tr key={idx} className="border-b border-slate-50">
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-slate-900">{stock.product_name}</p>
                        <p className="text-xs text-slate-500 font-mono">{stock.product_sku}</p>
                      </td>
                      <td className={`py-3 px-4 text-sm text-right tabular-nums font-medium ${
                        stock.quantity < stock.stock_min ? 'text-red-600' : 'text-slate-900'
                      }`}>
                        {stock.quantity}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500 text-right tabular-nums">
                        {stock.stock_min}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingWarehouse ? 'Editar Almacén' : 'Nuevo Almacén'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Código</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                disabled={!!editingWarehouse}
                required
                placeholder="ALM-001"
                data-testid="warehouse-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Nombre del almacén"
                data-testid="warehouse-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Dirección</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Dirección física"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-900 hover:bg-blue-800"
                disabled={saving}
                data-testid="warehouse-submit-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingWarehouse ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehousesPage;
